import { useIsKinderConnectCaptureEnabledForChildren } from './use-is-kinder-connect-capture-enabled-for-children';
import { useKinderConnectCaptureModalWithContext } from './use-kinder-connect-capture-modal-with-context';
/**
 * Combines {@link useIsKinderConnectCaptureEnabledForChildren} with the capture modal into the
 * "skip while loading, then either open capture or proceed directly" gate that every check-in/
 * check-out submission needs, so call sites don't each hand-roll the same three steps.
 *
 * @param childIds - The children involved in the check-in/check-out action. Pass a one-element
 * array for a single-child entry point.
 * @param siteId - The site the check-in/check-out is happening at, used only as a fallback if the
 * per-child answer errors. Pass an empty string if unavailable -- the fallback is then skipped.
 * @returns Whether capture is enabled/still loading for these children, and `runOrCapture` to gate
 * a submission behind it.
 * @example
 * const {runOrCapture} = useKinderConnectCaptureGate([child.id], siteId);
 * const handleSubmit = () => runOrCapture(kcData => dispatch(checkOutChild.action(child.id, pin, kcData)));
 */
export const useKinderConnectCaptureGate = (childIds, siteId) => {
    const { isEnabled, isLoading } = useIsKinderConnectCaptureEnabledForChildren(childIds, siteId);
    const kcCaptureModal = useKinderConnectCaptureModalWithContext();
    const runOrCapture = (onProceed) => {
        if (isLoading) {
            return;
        }
        if (isEnabled) {
            kcCaptureModal.show({ onCapture: onProceed });
            return;
        }
        onProceed();
    };
    return { isEnabled, isLoading, runOrCapture };
};
